import styled from '@emotion/styled';
import { useState, useLayoutEffect } from 'react';
import * as React from 'react';
import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import { inlinePositioning as inlinePositioningPlugin } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { tokens } from '@hotjar/hotsauce';

import { ZINDEX as REACT_MODAL_ZINDEX } from '@hotjar/webapp/components/GenericModal';

interface Props extends Omit<TippyProps, 'theme'> {
  overflowRef?: React.RefObject<HTMLElement>;
  inverted?: boolean;
  enabled?: boolean;
  boundary?: HTMLElement | null;
  maxWidth?: number;
  kind?: 'default' | 'unstyled';
}

/**
 * NOTE: put here any property from above type definition
 * to make sure it's not passed to the original Tippy component
 * which prints warning when it sees an unknown property
 */
const CUSTOM_PROP_NAMES = ['overflowRef', 'inverted', 'enabled', 'boundary', 'isXToolConnector'];

interface StyledProps extends TippyProps {
  inverted: boolean;
  kind: 'default' | 'unstyled';
}

function getBackground({ inverted }: StyledProps) {
  if (inverted) {
    return tokens.color.ui_background;
  }

  return tokens.color.neutral_800;
}

const StyledTooltip = styled(Tippy, {
  shouldForwardProp(propName: string): boolean {
    return !CUSTOM_PROP_NAMES.includes(propName);
  },
})<StyledProps>`
  text-transform: none;
  color: ${({ inverted }) =>
    inverted ? tokens.color.text_default : tokens.color.text_default_inverse};
  border: 0;
  padding: 8px 10px;
  font-size: 13px;
  word-break: break-word;
  word-wrap: break-word;
  background: ${getBackground};
  border-radius: 2px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);

  .tippy-content {
    padding: 0;
    white-space: pre-line;
    text-align: center;
  }

  &[data-placement^='top'] > .tippy-arrow::before {
    border-top-color: ${getBackground};
  }

  &[data-placement^='bottom'] > .tippy-arrow::before {
    border-bottom-color: ${getBackground};
  }

  &[data-placement^='left'] > .tippy-arrow::before {
    border-left-color: ${getBackground};
  }

  &[data-placement^='right'] > .tippy-arrow::before {
    border-right-color: ${getBackground};
  }

  ${({ kind }) =>
    kind === 'unstyled' &&
    `
      border: 0;
      padding: 0;
      box-shadow: none;
      border-radius: none;
      background-color: transparent;
      z-index: 100;

  `}
`;

export const Tooltip = React.forwardRef<HTMLElement, Props>(
  (
    {
      overflowRef,
      children,
      inlinePositioning,
      boundary,
      enabled = true,
      inverted = false,
      zIndex = REACT_MODAL_ZINDEX,
      maxWidth = 280,
      kind = 'default',
      ...props
    },
    ref,
  ) => {
    const [hasOverflow, setHasOverflow] = useState(!overflowRef);

    useLayoutEffect(() => {
      if (!overflowRef || !overflowRef.current) {
        return;
      }

      const { offsetWidth, scrollWidth } = overflowRef.current;
      setHasOverflow(offsetWidth < scrollWidth);
    }, [overflowRef, setHasOverflow]);

    return (
      <StyledTooltip
        maxWidth={maxWidth}
        disabled={!(enabled && hasOverflow)}
        inverted={inverted}
        kind={kind}
        zIndex={zIndex}
        popperOptions={
          boundary
            ? {
                modifiers: [
                  { name: 'preventOverflow', options: { boundary } },
                  {
                    name: 'flip',
                    options: {
                      boundary,
                      fallbackPlacements: ['right', 'left', 'top', 'bottom'],
                    },
                  },
                ],
              }
            : undefined
        }
        {...(inlinePositioning && { inlinePositioning, plugins: [inlinePositioningPlugin] })}
        {...props}
        ref={ref}
      >
        {children}
      </StyledTooltip>
    );
  },
);
